/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import {
    Configuration,
    ConfigurationParameters,
    MyShowsContentV2,
    MyShowsFilterList,
    MyShowsFolderV2,
    MyShowsItemsApi,
    MyShowsItemsGetV2Request,
    MyShowsItemV2,
    MyShowsRecordingV2,
} from 'client-code/myshows';

import { CommonHeaders } from '../CommonHeaders';
import { Constants } from '../constants';
import { IMyShowsServiceRepository } from '../interfaces';
import { Log } from '../logging/Log';
import { TMyShowsItemList, TMyShowsItemListParams, TServicesParams, TStorageInfo } from '../types';

export class MyShowsServiceRepositoryImplV2 implements IMyShowsServiceRepository {
    private static TAG: string = 'MyShowsServiceRepositoryImplV2';

    private mCommonHeaders: CommonHeaders;

    private mConfiguration: Configuration;

    private mServiceParams: TServicesParams;

    public constructor(params: TServicesParams) {
        this.mServiceParams = params;

        this.mCommonHeaders = new CommonHeaders('MyShows');

        const ConfigParams: ConfigurationParameters = {
            basePath: this.mServiceParams.basePath,
            apiKey: this.mServiceParams.accessToken,
            baseOptions: {
                timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                headers: this.mCommonHeaders.headers,
            },
        };

        this.mConfiguration = new Configuration(ConfigParams);
    }

    private getCommonRequestParams() {
        return {
            applicationName: this.mCommonHeaders.headers.ApplicationName,
            applicationFeatureArea: this.mCommonHeaders.headers.ApplicationFeatureArea,
            applicationVersion: this.mCommonHeaders.headers.ApplicationVersion,
            deviceType: this.mCommonHeaders.headers.DeviceType,
            productName: this.mCommonHeaders.headers.ProductName,
            bodyId: this.mServiceParams.bodyId,
            whiny: this.mServiceParams.whiny || 'false',
        };
    }

    private async getMyShowsItemListFromServiceV2({
        myShowsFilter,
        notCurrentlyAvailable,
        recentlyDeleted,
        streamingMovies,
        count,
        offset,
        orderBy,
        unifiedCollectionId,
        networkCallId,
    }: TMyShowsItemListParams): Promise<TMyShowsItemList> {
        const additionalParams = {
            ...(orderBy && { orderBy }),
            ...(offset && { offset }),
            ...(count && { count }),
            ...(unifiedCollectionId && { unifiedCollectionId }),
        };

        try {
            const requestParams: MyShowsItemsGetV2Request = {
                ...this.getCommonRequestParams(),
                timeZoneName: this.mServiceParams.timeZone,
                productName: this.mCommonHeaders.headers.ProductName,
                acceptLanguage: this.mCommonHeaders.headers['Accept-Language'],
                myShowsFilter,
                recentlyDeleted,
                notCurrentlyAvailable,
                streamingMovies,
                ...additionalParams,
                exclude: 'specials',
            };
            const myShowsItemListApi = new MyShowsItemsApi(this.mConfiguration);
            const myShowsItemListResponse =
                await myShowsItemListApi.myShowsItemsGetV2(requestParams);

            const totalMyShowsCount = parseInt(
                myShowsItemListResponse.headers['x-total-count'],
                10,
            );
            const myShows = myShowsItemListResponse.data;
            const lastSelectedItem = myShowsItemListResponse.data.selectedMyShowsItem;
            const { sortOrder } = myShowsItemListResponse.data;

            return {
                lastSelectedItem,
                myShows: myShows.myShows as Array<
                    MyShowsItemV2 & MyShowsContentV2 & MyShowsRecordingV2 & MyShowsFolderV2
                >,
                totalMyShowsCount,
                sortOrder,
                networkCallId,
            };
        } catch (error: any) {
            error.networkCallId = networkCallId;
            Log.e(
                MyShowsServiceRepositoryImplV2.TAG,
                'MyShowsServiceRepositoryImplV2::getMyShowsItemListFromServiceV2',
                error,
            );
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async getMyShowsItemList({
        myShowsFilter,
        notCurrentlyAvailable,
        recentlyDeleted,
        streamingMovies,
        count,
        offset,
        orderBy,
        unifiedCollectionId,
    }: TMyShowsItemListParams): Promise<TMyShowsItemList> {
        return this.getMyShowsItemListFromServiceV2({
            myShowsFilter,
            notCurrentlyAvailable,
            recentlyDeleted,
            streamingMovies,
            count,
            offset,
            orderBy,
            unifiedCollectionId,
        });
    }

    /**
     * @inheritdoc
     */
    public async getDiskSpace(networkCallId?: number): Promise<TStorageInfo> {
        throw new Error('Method not implemented.');
    }

    /**
     * @inheritdoc
     */
    public async getMyShowsFilterList(): Promise<MyShowsFilterList> {
        throw new Error('Method not implemented.');
    }

    /**
     * @inheritdoc
     */
    public async setLastSelectedItem(myShowsItemId: string, myShowsFilter?: string) {
        throw new Error('Method not implemented.');
    }
}
